import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const GuestOrderDetail = () => {
  const location = useLocation();
  const captureRef = useRef(null);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const orderId = searchParams.get("order_id") || "";
  const phone = searchParams.get("phone") || "";

  const [loading, setLoading] = useState(true);
  const [redirectUrl, setRedirectUrl] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          "/api/kiosk/guest_order_detail",
          {
            params: {
              order_id: orderId,
              phone,
            },
            withCredentials: true,
          }
        );

        const payload = res.data || {};

        if (payload.redirect) {
          setRedirectUrl(payload.redirect);
          return;
        }

        setData(payload);
      } catch (err) {
        setErrorMsg(
          err.response?.data?.msg ||
            err.response?.data?.message ||
            "주문 정보를 불러오지 못했습니다."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orderId, phone]);

  useEffect(() => {
    if (!loading && redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [loading, redirectUrl]);

  const order = data?.order || {};
  const items = Array.isArray(data?.items) ? data.items : [];
  const paid = Number(data?.paid || 0);
  const status = data?.status || "";
  const saveName =
    data?.save_name || `order_${orderId}`;

  const won = (n) =>
    `${Number(n || 0).toLocaleString("ko-KR")}원`;

  const getImageSrc = (path) => {
    if (!path) {
      return "/static/images/noimage.png";
    }

    const clean = String(path).trim();

    if (!clean) {
      return "/static/images/noimage.png";
    }

    if (
      clean.startsWith("http://") ||
      clean.startsWith("https://")
    ) {
      return clean;
    }

    if (clean.startsWith("/static/")) {
      return clean;
    }

    if (clean.startsWith("static/")) {
      return `/${clean}`;
    }

    if (clean.startsWith("/product_images/")) {
      return `/static${clean}`;
    }

    if (clean.startsWith("product_images/")) {
      return `/static/${clean}`;
    }

    return `/static/${clean.replace(/^\/+/, "")}`;
  };

  const handleSaveImage = async () => {
    try {
      const html2canvasModule =
        await import("html2canvas");

      const html2canvas =
        html2canvasModule.default;

      const area = captureRef.current;
      if (!area) return;

      const canvas = await html2canvas(
        area,
        {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          scrollX: 0,
          scrollY: -window.scrollY,
          windowWidth:
            document.documentElement.clientWidth,
          windowHeight:
            document.documentElement.clientHeight,
        }
      );

      const link =
        document.createElement("a");

      link.download = `${saveName}.png`;
      link.href =
        canvas.toDataURL("image/png");

      link.click();
    } catch (e) {
      alert(
        "이미지 저장 중 오류가 발생했습니다."
      );
    }
  };

  if (loading) {
    return (
      <>
        <style>{`
          body {
            background:#f6f7f9;
          }

          .card {
            border:0;
            border-radius:14px;
          }
        `}</style>

        <div className="container py-4">
          <div className="card p-3">
            주문 정보를 불러오는 중...
          </div>
        </div>
      </>
    );
  }

  if (errorMsg) {
    return (
      <>
        <style>{`
          body {
            background:#f6f7f9;
          }

          .card {
            border:0;
            border-radius:14px;
          }
        `}</style>

        <div className="container py-4">
          <div className="card p-3 text-danger">
            {errorMsg}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        body {
          background:#f6f7f9;
        }

        .card {
          border:0;
          border-radius:14px;
        }

        #capture-area {
          background:#f6f7f9;
        }

        .thumb {
          width:54px;
          height:54px;
          object-fit:cover;
          border-radius:10px;
          background:#eee;
          flex:0 0 54px;
          display:block;
        }

        .muted {
          color:#6c757d;
        }

        .badge-custom {
          display:inline-block;
          font-size:12px;
          padding:6px 10px;
          border-radius:999px;
          background:#111;
          color:#fff;
          white-space:nowrap;
          flex-shrink:0;
          line-height:1.2;
        }

        .save-hint {
          font-size:12px;
          color:#6c757d;
        }

        .receipt-link {
          color:#0d6efd;
          text-decoration:none;
          white-space:nowrap;
        }

        .receipt-link:hover {
          text-decoration:underline;
        }

        .option-line {
          color:#6c757d;
          font-size:12px;
          line-height:1.35;
        }

        .normal-price {
          color:#999;
          font-size:12px;
          text-decoration:line-through;
        }

        .sale-price {
          color:#dc3545;
          font-size:13px;
          font-weight:800;
        }

        .option-total {
          color:#0d6efd;
          font-size:12px;
        }

        .unit-price {
          color:#6c757d;
          font-size:12px;
        }

        .product-row + .product-row {
          border-top:1px solid #eee;
          padding-top:12px;
        }
      `}</style>

      <div
        className="container py-4"
        id="capture-area"
        ref={captureRef}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="fw-bold m-1" />
          </div>

          <a
            href="/kiosk/guest-order-lookup"
            className="btn btn-outline-secondary btn-sm"
          >
            다른주문조회
          </a>
        </div>

        <div className="card p-3 mb-3">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div>
              <h4 className="fw-bold mb-3">
                주문상세
              </h4>

              <div className="small muted">
                주문번호:{" "}
                {Number(order.id || 0)}
              </div>

              <div className="small muted">
                주문일시:{" "}
                {order.order_date || ""}
              </div>

              <div className="small muted">
                가게:{" "}
                {order.shop_name || ""}
              </div>
            </div>

            <span className="badge-custom">
              {status}
            </span>
          </div>

          <hr className="my-3" />

          <div className="small">
            <div className="mb-1">
              <b>주문자</b>{" "}
              {order.buyer_name || ""}
            </div>

            <div className="mb-1">
              <b>휴대폰</b>{" "}
              {order.buyer_phone || ""}
            </div>

            {order.buyer_email ? (
              <div className="mb-1">
                <b>이메일</b>{" "}
                {order.buyer_email}
              </div>
            ) : null}

            <div className="mb-1">
              <b>결제수단</b>{" "}
              {order.payment_method || ""}
            </div>

            <div className="mb-2 fw-bold">
              결제금액: {won(paid)}{" "}
              <a
                className="receipt-link"
                href={`/kiosk/receipt-print?order_id=${Number(
                  orderId || 0
                )}`}
              >
                [영수증 보기]
              </a>
            </div>
          </div>
        </div>

        <div className="card p-3 mb-3">
          <h5 className="fw-bold mb-2">
            상품
          </h5>

          {items.length === 0 ? (
            <div className="text-muted">
              주문 상품이 없습니다.
            </div>
          ) : (
            <div className="d-grid gap-3">
              {items.map((it, idx) => {
                const options =
                  Array.isArray(it.options)
                    ? it.options
                    : [];

                const qty = Number(
                  it.quantity ??
                    it.qty ??
                    0
                );

                const originalPrice = Number(
                  it.original_price ??
                    it.normal_price ??
                    it.price ??
                    0
                );

                const productPrice = Number(
                  it.product_price ??
                    it.display_price ??
                    it.price ??
                    0
                );

                const optionTotal = Number(
                  it.option_total ??
                    it.option_price ??
                    0
                );

                const unitPrice = Number(
                  it.unit_price ??
                    (
                      productPrice +
                      optionTotal
                    )
                );

                const lineTotal = Number(
                  it.line ??
                    it.line_total ??
                    it.subtotal ??
                    (
                      unitPrice *
                      qty
                    )
                );

                const isSale =
                  Boolean(it.is_sale) ||
                  (
                    originalPrice > 0 &&
                    productPrice > 0 &&
                    productPrice <
                      originalPrice
                  );

                const img =
                  getImageSrc(
                    it.image_path
                  );

                return (
                  <div
                    className="product-row d-flex gap-3 align-items-start"
                    key={`${
                      it.order_item_id ||
                      it.product_id ||
                      idx
                    }-${idx}`}
                  >
                    <img
                      className="thumb"
                      src={img}
                      alt={
                        it.product_name ||
                        it.name ||
                        ""
                      }
                      onError={(e) => {
                        e.currentTarget.onerror =
                          null;

                        e.currentTarget.src =
                          "/static/images/noimage.png";
                      }}
                    />

                    <div className="flex-grow-1">
                      <div className="fw-bold">
                        {it.product_name ||
                          it.name ||
                          `상품#${
                            it.product_id ||
                            ""
                          }`}
                      </div>

                      {options.length > 0 ? (
                        <div className="mt-1">
                          {options.map(
                            (opt, optIdx) => {
                              const groupName =
                                opt.option_group_name ||
                                opt.group_name ||
                                "";

                              const itemName =
                                opt.option_item_name ||
                                opt.item_name ||
                                "";

                              const optionPrice =
                                Number(
                                  opt.option_price ??
                                    opt.extra_price ??
                                    0
                                );

                              return (
                                <div
                                  className="option-line"
                                  key={`${
                                    it.order_item_id ||
                                    it.product_id
                                  }-${optIdx}`}
                                >
                                  ·{" "}
                                  {groupName
                                    ? `${groupName}: `
                                    : ""}
                                  {itemName}

                                  {optionPrice !==
                                  0
                                    ? ` (${
                                        optionPrice >
                                        0
                                          ? "+"
                                          : ""
                                      }${won(
                                        optionPrice
                                      )})`
                                    : ""}
                                </div>
                              );
                            }
                          )}
                        </div>
                      ) : null}

                      <div className="mt-1">
                        {isSale ? (
                          <div className="normal-price">
                            정상가{" "}
                            {won(
                              originalPrice
                            )}
                          </div>
                        ) : null}

                        <div
                          className={
                            isSale
                              ? "sale-price"
                              : "small muted"
                          }
                        >
                          상품{" "}
                          {won(productPrice)}
                        </div>

                        {optionTotal > 0 ? (
                          <div className="option-total">
                            옵션 +
                            {won(optionTotal)}
                          </div>
                        ) : null}

                        <div className="unit-price">
                          단가{" "}
                          {won(unitPrice)}
                          {" · "}
                          수량 {qty}
                        </div>
                      </div>
                    </div>

                    <div className="fw-bold text-nowrap">
                      {won(lineTotal)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="d-grid gap-2 mb-0">
          <button
            className="btn btn-primary w-100 py-3 fw-bold"
            id="btnSaveImg"
            onClick={handleSaveImage}
          >
            이미지(PNG)로 저장
          </button>

          <div className="save-hint text-center">
            버튼을 누르면 현재 주문내역
            화면이 이미지로 저장됩니다.
          </div>
        </div>
      </div>
    </>
  );
};

export default GuestOrderDetail;
